import React from 'react';

import { buttonClasses, paginationClasses, tooltipClasses } from '@private/components';
import { DataGrid, dataGridClasses, IGridProps } from '@private/data-grid';
import { getBorderBase, getFontBase, getPrefixedVar } from '@private/payment';
import styled, { css, keyframes } from 'styled-components';

import { getAbsoluteLoaderTheme } from 'components/AbsoluteLoader';
import { Container as HeaderCellContainer } from 'components/Grids/DataGrid/Cells/HeaderCell/styled';
import Popover from 'components/Popover';
import { getScrollbarTheme } from 'components/Scrollbar';
import { ICONS_MAP } from 'constants/icons';

const prefix = ['components', 'dataGrid'];

const skeletonAnimation = keyframes`
  0% {
    background: ${getPrefixedVar(prefix, 'skeleton', 'bgStart')};
  }

  100% {
    background: ${getPrefixedVar(prefix, 'skeleton', 'bgEnd')};
  }
`;

interface IStyledDataGridExtraProps {
  $expandedContentHeight?: number;
  emptyPagePadding?: number;
  minHeight?: number;
}

// styled() drops extra props typing for generic components, so the cast below
// restores both the grid generic and the styling props at the call site
const StyledDataGridBase = styled(DataGrid)<IStyledDataGridExtraProps>`
  min-height: ${({ minHeight }) => minHeight || 500}px;
  flex: 1 1 auto;

  /* expanded mode: the grid sizes to its rows and the page scrolls instead of the grid */
  ${({ $expandedContentHeight }) =>
    $expandedContentHeight &&
    css`
      min-height: auto;
      flex: 0 0 auto;

      > ${dataGridClasses.gridWrapper} {
        height: ${$expandedContentHeight}px;
        flex: 0 0 ${$expandedContentHeight}px;
      }
    `}

  ${({ notVirtualized }) =>
    notVirtualized
      ? css`
          ${dataGridClasses.flexColumnsContainer} {
            ${getBorderBase([...prefix, 'container'])};

            .left,
            .center,
            .right {
              ${getScrollbarTheme()};
            }
          }
        `
      : css`
          ${dataGridClasses.columnsContainer} {
            .left,
            .center,
            .right {
              ${getScrollbarTheme()};
            }
          }

          ${dataGridClasses.gridWrapper} {
            ${getBorderBase([...prefix, 'container'])};
          }
        `}

  .left, .right {
    ${HeaderCellContainer} {
      line-height: 1;
    }
  }

  ${dataGridClasses.progressBar} {
    ${getAbsoluteLoaderTheme(['components', 'absoluteLoader'])};
  }

  ${dataGridClasses.skeleton} {
    ${getBorderBase([...prefix, 'skeleton'])};

    animation: ${skeletonAnimation} 1s linear infinite alternate;
  }

  ${dataGridClasses.topToolbarContainer}, ${dataGridClasses.bottomToolbarContainer} {
    margin-right: 0;
    z-index: 3;
  }

  ${dataGridClasses.topToolbarContainer} {
    z-index: 20;
    margin-bottom: 16px;

    @media (width <= 1000px) {
      margin-bottom: 12px;
    }
  }

  ${dataGridClasses.bottomToolbarContainer} {
    @media (width <= 1000px) {
      margin-top: 8px;
    }
  }

  ${dataGridClasses.settingsButton} {
    width: 36px;
    height: 36px;

    svg {
      width: auto;
      height: auto;
    }
  }

  ${dataGridClasses.settingsButton},
  ${dataGridClasses.bottomToolbarContainer} ${buttonClasses.button} {
    ${getBorderBase([...prefix, 'pagination', 'button', 'base'])};

    &:hover {
      color: ${getPrefixedVar(prefix, 'pagination', 'button', 'hover', 'text')};
      background: ${getPrefixedVar(prefix, 'pagination', 'button', 'hover', 'bg')};
    }

    &:active {
      color: ${getPrefixedVar(prefix, 'pagination', 'button', 'pressed', 'text')};
      background: ${getPrefixedVar(prefix, 'pagination', 'button', 'pressed', 'bg')};
    }
  }

  ${paginationClasses.prevButton}, ${paginationClasses.nexButton} {
    color: ${getPrefixedVar(prefix, 'pagination', 'button', 'base', 'text')};

    &[data-isdisabled='true'] {
      color: ${getPrefixedVar(prefix, 'pagination', 'button', 'disabled', 'text')};
      background: ${getPrefixedVar(prefix, 'pagination', 'button', 'disabled', 'bg')};
    }
  }

  ${paginationClasses.text} {
    color: ${getPrefixedVar(prefix, 'pagination', 'text')};
  }

  ${dataGridClasses.notFoundContainer} {
    padding: ${({ emptyPagePadding }) => emptyPagePadding || 50}px 0;
  }

  ${dataGridClasses.headerRowContainer} {
    background: ${getPrefixedVar(prefix, 'headerCell', 'bg')};
  }

  ${dataGridClasses.flexRow}

  [data-variant='odd'] {
    background: ${getPrefixedVar(prefix, 'rowCell', 'container', 'odd')};
  }

  ${dataGridClasses.flexRow}

  [data-variant='even'] {
    background: ${getPrefixedVar(prefix, 'rowCell', 'container', 'even')};
  }

  ${dataGridClasses.gridWrapper} {
    &.left,
    &.right {
      box-shadow: 0 0 10px 0 ${getPrefixedVar(prefix, 'pinColumnShadow')};
      z-index: 2;
    }
  }

  ${dataGridClasses.columnsContainer} {
    .left,
    .right {
      box-shadow: 0 0 25px 0 ${getPrefixedVar(prefix, 'pinColumnShadow')};
      z-index: 2;
    }

    @media (width <= 1300px) {
      .left,
      .right {
        max-width: 250px;
      }
    }
  }
`;

export const StyledDataGrid = StyledDataGridBase as unknown as <T>(
  props: IGridProps<T> & IStyledDataGridExtraProps,
) => React.ReactElement;

export const TableSettings = styled.div`
  flex: 1 1 auto;
  width: 100%;
`;

export const SettingsColumn = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const dragIconPrefix = [...prefix, 'columnsFilter', 'popover', 'dragIcon'];

const DragDropIcon = css`
  .dragItemBody {
    fill: ${getPrefixedVar(dragIconPrefix, 'dragItemBody')};
  }

  .dragItemZone {
    fill: ${getPrefixedVar(dragIconPrefix, 'dragItemZone')};
  }

  .dragDots {
    fill: ${getPrefixedVar(dragIconPrefix, 'dragDots')};
  }

  .dragTextZone {
    fill: ${getPrefixedVar(dragIconPrefix, 'dragTextZone')};
  }

  .switch {
    fill: ${getPrefixedVar(dragIconPrefix, 'switch')};
  }

  .switchDot {
    fill: ${getPrefixedVar(dragIconPrefix, 'switchDot')};
  }

  .dragItemBorder {
    stroke: ${getPrefixedVar(dragIconPrefix, 'dragItemBorder')};
  }

  .arrow {
    stroke: ${getPrefixedVar(dragIconPrefix, 'arrow')};
  }
`;

export const DragDropLeftIcon = styled(ICONS_MAP.DragDropLeft)`
  ${DragDropIcon};
`;

export const DragDropRightIcon = styled(ICONS_MAP.DragDropRight)`
  ${DragDropIcon};
`;

export const TopBarRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

export const RightTopBarColumn = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const rowCellPrefix = ['components', 'dataGrid', 'rowCell'];

const getColumnPrefix = ({
  isEven,
  isMainColumn,
  isSelected,
}: {
  isEven: boolean;
  isMainColumn: boolean;
  isSelected: boolean;
}) => {
  const prefixes = [...rowCellPrefix, 'container'];

  if (!isMainColumn && isSelected) {
    return prefixes.concat('selected');
  }
  if (isMainColumn) {
    return prefixes.concat(isEven ? 'mainColumnEven' : 'mainColumnOdd');
  }
  return prefixes.concat(isEven ? 'even' : 'odd');
};

export const RowCellContainer = styled.div<{
  $center?: boolean;
  $index?: number;
  $isMainColumn?: boolean;
  $isMulti?: boolean;
  $isPointer?: boolean;
  $isSelected?: boolean;
}>`
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: ${({ $center }) => ($center ? 'center' : 'flex-start')};
  padding: 10px 16px;
  column-gap: 5px;
  border-bottom-style: solid;
  ${getFontBase([...rowCellPrefix, 'text'])};
  ${getBorderBase([...rowCellPrefix, 'container'])};

  cursor: ${({ $isPointer }) => ($isPointer ? 'pointer' : 'auto')};
  background: ${({ $index, $isMainColumn, $isSelected }) => {
    return getPrefixedVar(
      getColumnPrefix({
        isEven: !!($index && $index % 2 !== 0),
        isMainColumn: !!$isMainColumn,
        isSelected: !!$isSelected,
      }),
    );
  }};
`;

export const MultiCellContainer = styled(RowCellContainer)<{
  $isStretched?: boolean;
}>`
  ${({ $isStretched }) =>
    $isStretched &&
    css`
      > * {
        flex-shrink: 0;
      }
    `}
`;

export const HelperPopover = styled(Popover)`
  ${tooltipClasses.message} {
    padding: 10px;
    max-width: 400px;
    max-height: 200px;
    overflow: auto;
    word-break: normal;
    overflow-wrap: break-word;
  }
`;

export const HelperPopoverContainer = styled.div`
  max-width: 100%;
  flex: 1 1 70%;
`;

export const CopyCellPopoverContainer = styled.div`
  width: 100%;
`;
